import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'sonner';
import { Check, Plus, Repeat, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { ymdLocal } from '@/shared/lib/ymd-local';
import { AppointmentFormProvider } from '@/scheduling/components/appointments/appointment-form-provider';
import { PanelHeader } from '@/scheduling/components/appointments/sections/panel-header';
import { PanelFooter } from '@/scheduling/components/appointments/sections/panel-footer';
import { DatePickerSection } from '@/scheduling/components/appointments/sections/date-picker-section';
import { NoteSection } from '@/scheduling/components/appointments/sections/note-section';
import { RecurrenceSection } from '@/scheduling/components/appointments/sections/recurrence-section';
import { RecurrencePickerView } from '@/scheduling/components/appointments/sections/recurrence-picker-view';
import {
  usePatientSearch,
  type PatientSearchResult,
} from '@/services/scheduling/use-patient-search';
import { useServiceFees, type ServiceFee } from '@/services/billing/use-service-fees';
import { defaultDeviceProduct, useDeviceProducts } from '@/scheduling/services/use-device-products';
import { useResourceAvailability } from '@/scheduling/services/use-device-availability';
import { useDeviceCalendarSlots } from '@/scheduling/services/use-device-calendar-slots';
import { useBookDevice } from '@/scheduling/services/use-book-device';
import { useUpdateDeviceAppointment } from '@/scheduling/services/use-update-device-appointment';
import {
  buildEditRecurringPayload,
  type RecurringScope,
} from '@/scheduling/components/appointments/edit-recurring-scope-dialog';
import {
  buildDeviceUpdateRequest,
  currentDeviceProductId,
  deviceIdOf,
  deviceNameOf,
  isEditableDeviceBooking,
  patientIdOf,
  patientLabelOf,
  seedDeviceEditServices,
  withBookedStart,
} from '@/scheduling/lib/device-edit-request';
import { DeviceSeriesScopeDialog } from './device-series-scope-dialog';
import { useNow } from '@/shared/hooks/use-now';
import {
  fitsWindow,
  formatClock,
  isNotBookable,
  localNoZ,
  parseAvailability,
  parseWallClock,
  startHasPassed,
  windowsForDay,
} from '@/scheduling/lib/device-availability';
import { CustomTimePopover } from '@/scheduling/components/shared/custom-time-popover';
import { SlotGroupList } from '@/scheduling/components/shared/slot-group-list';
import { toSlotItems, type SlotItem } from '@/scheduling/lib/slot-selection';
import {
  buildDeviceBookingRequest,
  deviceBookingIsInvoiceable,
  resolveDeviceLocation,
  toDeviceRecurring,
} from '@/scheduling/lib/device-booking-request';
import { DevicePickerGrid } from '@/scheduling/components/shared/device-picker-grid';
import {
  classifyDeviceBookingFailure,
  type DeviceBookingFailure,
} from '@/scheduling/lib/device-booking-errors';
import type { OrgLocation } from '@/auth/auth-api';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { AppointmentResponse, CalendarDevice } from '@/types/scheduling-api';

/**
 * BookDevicePanel — "Book a device" (PROD-2007, Scheduling PR #1964),
 * mounted as the INLINE BOOKING RAIL since PROD-2186.
 *
 * Andrew, 2026-09-13: "create device became popup which is wrong" … "when
 * pressing create, the panel should appear as the practitioner one exactly".
 * It was a shadcn Dialog; it is now the same rail `CreateAppointmentPanel` is,
 * taking the same `edge` / `layout` from the same per-org gate
 * (`use-booking-rail-layout.ts`) and sitting in the same DOM slot, so:
 *   - it does not cover the calendar being booked against;
 *   - reading / focus order is the page's own — no focus trap, nothing
 *     `aria-hidden` behind it;
 *   - it occupies the ONE rail slot (single-rail invariant, Flutter parity),
 *     so it cannot coexist with Create / InlineEdit / Block.
 * It also retires an unreported bug of the dialog era: the body was clamped
 * at `max-h-[70vh]` inside a viewport-capped dialog content box, so at common
 * laptop heights the footer — carrying the only Book button — fell below the
 * fold. A `flex-shrink-0` footer in an `h-full` column cannot be pushed off.
 *
 * The FLOW is unchanged — this is a presentation change, not a semantics one.
 * The booking contract is still `ProviderId = Guid.Empty` /
 * `ParticipationReferenceType.Device` via `buildDeviceBookingRequest`, and the
 * product pricing precedence (the product's own `serviceId` — PROD-2165 — else
 * its `billingProfileId` — PROD-2154 — with the caller's added services billed
 * on top) is untouched. Section order now mirrors the practitioner rail:
 *   1. Date        — the SHARED `DatePickerSection`.
 *   2. Device      — the practice's active devices, as SEARCH OVER CARDS
 *                    (PROD-2193); stands exactly where the practitioner
 *                    rail's `ProviderSection` stands, wearing the same
 *                    control.
 *   3. Product     — the device's LIVE appointment profiles; default preselected.
 *   4. Time        — the practitioner rail's time section (PROD-2455): free
 *                    starts READ from `DeviceCalendarSlots`, a SELECTED SLOT
 *                    card with a Custom time entry, and Morning / Afternoon /
 *                    Evening grouping, all through the shared components. End
 *                    is derived server-side from the chosen product.
 *   5. Patient     — the same patient search the create rail uses.
 *   6. Services    — what the booking invoices (ruling 2026-09-12: a device
 *                    booking MUST invoice). Required only when the chosen
 *                    product prices neither by its own service nor by a
 *                    billing profile; available either way for extras.
 *   7. Note        — the SHARED `NoteSection`.
 *   8. Recurrence  — PROD-2383, the SHARED configurator and summary card the
 *                    practitioner rail uses (`RecurrencePickerView` /
 *                    `RecurrenceSection`), checked against the DEVICE's
 *                    calendar. A course books every session as its own
 *                    appointment with its own invoice.
 * No provider field exists anywhere on this rail, and since PROD-2193 no
 * LOCATION field either — a machine stands in the practice, so the location
 * is resolved (`resolveDeviceLocation`) rather than asked.
 *
 * The parent remounts the rail per open (`key`) so initial props are read
 * once; there is no state-in-effect.
 *
 * EDIT MODE (PROD-2526). Andrew, 2026-09-23: "rescheduling is totally wrong,
 * it should be like the reschedule of the provider appointments … the UI
 * (Design) is should open the same panel of the create/edit device booking to
 * allow update". The practitioner has ONE appointment panel for create, edit
 * and reschedule (`CreateAppointmentPanel` with `mode` + `editAppointment`);
 * this rail follows the same pattern: pass `editAppointment` and it opens
 * prefilled from the booking — device, product, patient, date, time, services,
 * note — and saves through the DEVICE edit route (`useUpdateDeviceAppointment`,
 * `PUT /api/Appointment/Resource/{id}`). It replaces the separate
 * `edit-device-panel.tsx` rail, which saved through the provider route (500 on
 * every call) and still had the old flat time list (PROD-2457). What that rail
 * did that this one did not is carried over here, not dropped:
 *   - device and patient are FIXED, shown and not asked (the server refuses a
 *     change: DeviceImmutable / PatientImmutable);
 *   - the booking's own current slot stays on the list, marked `· now`, even
 *     when it is in the past, so a past session stays editable;
 *   - a booking whose product was ARCHIVED says so (`book-device-archived-product`);
 *   - a longer product at the booked start warns that the server may refuse it
 *     (`book-device-duration-warning`);
 *   - a Cancelled / Fulfilled / NoShow booking is locked (`book-device-locked`);
 *   - the services open as the invoice bills them, minus the product's own
 *     service, which is the unremovable included row.
 * A session of a recurring course asks which sessions a time change reaches
 * (`DeviceSeriesScopeDialog`: this / following / all — the practitioner series
 * menu's three choices).
 */
export interface BookDevicePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Which screen edge the rail sits on — decides which side its divider
   * border draws. Same prop, same meaning and same default as
   * `CreateAppointmentPanel`; FrameScheduling passes the resolved value.
   */
  edge?: 'left' | 'right';
  /**
   * Geometry mode (per-org gate, `use-booking-rail-layout.ts`):
   * 'classic' = fixed 380px, 'wide' = clamp(420px,33vw,560px). Same prop,
   * same meaning and same default as `CreateAppointmentPanel`.
   */
  layout?: 'classic' | 'wide';
  organizationId: string;
  practiceId: string;
  userId: string;
  /** Active devices of the practice (ordered by the caller). */
  devices: CalendarDevice[];
  /** Device preselected from a column header / empty cell. */
  initialDeviceId?: string | null;
  /** Local no-Z start from an empty-cell double-click. */
  initialStartISO?: string | null;
  /** The calendar's day — the default booking day. */
  selectedDate: Date;
  /**
   * Practice locations. Still the SOURCE of the booking's location — the
   * invoice is priced by it — but no longer a control: PROD-2193 resolves
   * "Office" out of this list instead of asking (see `resolveDeviceLocation`).
   */
  locations: OrgLocation[];
  onBooked?: (appointmentId: string) => void;
  /**
   * PROD-2526 — an existing device booking (the loaded PreLoad). Present, the
   * rail opens in EDIT mode, prefilled from it, and Save goes through
   * `PUT /api/Appointment/Resource/{id}`. Absent, it books, as before.
   */
  editAppointment?: AppointmentResponse | null;
  /** Header wording only — Reschedule and Edit open the same edit mode. */
  editIntent?: 'edit' | 'reschedule';
  /** The booking's NEW start (local no-Z) after an edit saved, so the calendar can follow it. */
  onSaved?: (newStart: string | null) => void;
  /**
   * PROD-2550 — whether a successful EDIT closes the rail. Same prop, same
   * meaning and same default as `CreateAppointmentPanel`'s: the calendar rail
   * closes itself on save, and the full-page `ViewAppointmentPanelPage` passes
   * `false` so the user stays on the appointment (its tabs refresh from the
   * invalidated PreLoad). Cancel still closes through `onOpenChange(false)`.
   * Booking (create mode) always closes.
   */
  closeOnSaveSuccess?: boolean;
}

interface SelectedService {
  id: string;
  label: string;
}

/**
 * Selection chips (product, free window). These are `<label>`s wrapping a
 * visually-hidden native radio rather than buttons with `role="radio"`:
 * arrow-key navigation, roving tab stop, the checked state and the focus ring
 * all come from the platform instead of hand-rolled key handling (review #3,
 * and CLAUDE.md's "don't re-implement a radio group as styled buttons").
 * `has-[:focus-visible]` moves the ring from the hidden input onto the chip.
 */
const CHIP_BASE =
  'inline-flex cursor-pointer items-center gap-2 rounded-card border px-3 py-1.5 text-sm ' +
  'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-primary has-[:focus-visible]:ring-offset-1';
const CHIP_ON = 'border-brand-primary bg-[var(--accent-active-bg)] text-text-primary';
const CHIP_OFF = 'border-[var(--surface-line)] text-text-secondary hover:bg-grey-100';

function dayBounds(ymd: string): { from: Date; to: Date; day: Date } {
  const [y, m, d] = ymd.split('-').map(Number);
  const day = new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
  return {
    day,
    from: day,
    to: new Date(y, (m ?? 1) - 1, d ?? 1, 23, 59, 59, 0),
  };
}

function serviceLabel(svc: ServiceFee): string {
  const code = svc.procedureCode?.code ?? '';
  const name = svc.procedureCode?.displayName ?? svc.shortDescription ?? '';
  const fee = svc.fees?.[0]?.fee;
  return [code, name].filter(Boolean).join(' — ') + (fee != null ? ` · ${fee}` : '');
}

function patientLabel(p: PatientSearchResult): string {
  return `${p.firstName} ${p.lastName}`.trim();
}

/**
 * Ask the slot feed for the whole remaining day rather than the Devices-tab
 * strip's three "Next" chips (PROD-2455). 100 is the same ceiling the
 * practitioner rail sends to `Appointment/Slots`; a device on the shortest
 * product the platform allows cannot reach it in a 24-hour day.
 */
const WHOLE_DAY_SLOT_COUNT = 100;

/**
 * The one device row out of the slot feed's response.
 *
 * Matched case-INSENSITIVELY: device ids reach this rail from the calendar's
 * own `CalendarDevice[]`, which have been observed carrying the upper-case GUID
 * spelling, while the slot feed answers in lower case. A case-sensitive compare
 * silently returned no row, which reads on screen as "this device has nothing
 * free today" — the worst possible failure mode for this particular query.
 * The single-row fallback covers a feed that ignores the `devices` filter: we
 * asked for exactly one, so one row back is that device by construction.
 */
function deviceRow(
  rows: readonly { id: string; slots: { start: string; end: string }[] }[] | undefined,
  deviceId: string,
) {
  if (!rows || rows.length === 0) return undefined;
  const wanted = deviceId.toLowerCase();
  return rows.find((r) => (r.id ?? '').toLowerCase() === wanted) ?? (rows.length === 1 ? rows[0] : undefined);
}

/** Device ids arrive in both GUID spellings (see `deviceRow`). */
function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

/** Start-of-day ISO the shared date section is seeded with. */
function seedSelectedDateIso(initialStartISO: string | null, selectedDate: Date): string {
  const base = parseWallClock(initialStartISO) ?? selectedDate;
  const d = new Date(base);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * The rail shell — geometry only, the same expression
 * `CreateAppointmentPanel` uses so the two can never drift apart. Holds no
 * hooks (the `!open` early return precedes them, same as the practitioner
 * rail); all state lives in `BookDeviceRailBody`.
 */
export function BookDevicePanel({
  open,
  edge = 'right',
  layout = 'wide',
  ...props
}: BookDevicePanelProps) {
  if (!open) return null;

  return (
    <div
      data-testid="book-device-rail"
      data-mode={props.editAppointment ? 'edit' : 'create'}
      className={cn(
        'flex-shrink-0 border-[var(--surface-line)] bg-[var(--surface-read)] flex flex-col h-full overflow-hidden',
        layout === 'wide' ? 'w-[clamp(424px,33vw,560px)]' : 'w-[384px]',
        edge === 'right' ? 'border-l' : 'border-r',
      )}
    >
      {/* The shared Date / Note sections are form-context components. This
          provider is a THIN host for the two fields they own (`selectedDate`,
          `comment`) so the device rail can reuse them verbatim instead of
          forking a second date strip and note card — it fires no queries of
          its own, and nothing practitioner-shaped is ever read back out of it
          (see `buildDeviceBookingRequest` for what actually ships). */}
      <AppointmentFormProvider
        initialValues={{
          selectedDate: seedSelectedDateIso(
            props.editAppointment?.start ?? props.initialStartISO ?? null,
            props.selectedDate,
          ),
          comment: props.editAppointment?.comment ?? '',
        }}
      >
        <BookDeviceRailBody {...props} />
      </AppointmentFormProvider>
    </div>
  );
}

type BookDeviceRailBodyProps = Omit<BookDevicePanelProps, 'open' | 'edge' | 'layout'>;

function BookDeviceRailBody({
  onOpenChange,
  organizationId,
  practiceId,
  userId,
  devices,
  initialDeviceId = null,
  initialStartISO = null,
  locations,
  onBooked,
  editAppointment = null,
  editIntent = 'edit',
  onSaved,
  closeOnSaveSuccess = true,
}: BookDeviceRailBodyProps) {
  const initialStart = useMemo(() => parseWallClock(initialStartISO), [initialStartISO]);
  // PROD-2526 — edit mode: the booking this rail was opened on, or null to book.
  const edit = editAppointment;
  const bookedStart = useMemo(() => (edit ? parseWallClock(edit.start) : null), [edit]);
  const bookedProductId = edit ? currentDeviceProductId(edit) : null;
  const locked = !!edit && !isEditableDeviceBooking(edit);

  // The shared date section owns the day; everything below reads it.
  const { watch, getValues, setValue } = useFormContext<AppointmentFormValues>();
  const selectedDateIso = watch('selectedDate');
  /**
   * PROD-2383 — the recurrence the SHARED configurator wrote to this rail's
   * thin form host, exactly where it writes it on the practitioner rail.
   */
  const recurring = watch('recurring');
  /** Which body the rail shows: the booking, or the recurrence configurator in its place. */
  const [view, setView] = useState<'book' | 'recurrence'>('book');
  const dateYmd = useMemo(
    () => ymdLocal(selectedDateIso ? new Date(selectedDateIso) : new Date()),
    [selectedDateIso],
  );

  const [deviceId, setDeviceId] = useState<string>(
    () => (edit ? deviceIdOf(edit) : initialDeviceId) ?? '',
  );
  const [productId, setProductId] = useState<string | null>(null);
  const [pickedStart, setPickedStart] = useState<Date | null>(() =>
    edit ? parseWallClock(edit.start) : initialStart,
  );
  const [patient, setPatient] = useState<PatientSearchResult | null>(null);
  const [patientQuery, setPatientQuery] = useState('');
  /**
   * `null` = still the booking's own services (edit mode, untouched): derived
   * from the loaded invoice on every render rather than copied into state, so
   * the seed can wait for the product list without an effect.
   */
  const [services, setServices] = useState<SelectedService[] | null>(() => (edit ? null : []));
  const [scopeOpen, setScopeOpen] = useState(false);
  const [serviceQuery, setServiceQuery] = useState('');
  const [refusal, setRefusal] = useState<{
    ymd: string;
    failure: DeviceBookingFailure;
    nextFree: Date | null;
  } | null>(null);

  // Changing the day used to clear the picked window and the refusal through
  // the date input's own onChange. The day now belongs to the SHARED date
  // section, so both are DERIVED against it instead — same visible result,
  // and no state written from an effect (the repo's set-state-in-effect rule).
  const start = pickedStart && ymdLocal(pickedStart) === dateYmd ? pickedStart : null;
  const failure = refusal && refusal.ymd === dateYmd ? refusal.failure : null;
  const nextFree = refusal && refusal.ymd === dateYmd ? refusal.nextFree : null;

  // Edit mode: the booked device may not be on the BOOKABLE list (no product
  // left, hours removed), and it is fixed anyway — so it is never looked up
  // into oblivion; its own participant names it.
  const device: CalendarDevice | null =
    devices.find((d) => sameId(d.id, deviceId)) ??
    (edit && deviceId
      ? {
          id: deviceId,
          name: deviceNameOf(edit) ?? 'This device',
          type: 'device',
          practiceId,
          status: 'active',
        }
      : null);
  // No location control (PROD-2193) — "of course it will be office". Resolved
  // from the same `locations` prop the dropdown used to render. An existing
  // booking keeps the location it was priced at.
  const location = useMemo(() => resolveDeviceLocation(locations), [locations]);
  const locationId = edit ? (edit.location?.id ?? null) : (location?.id ?? null);

  // ── Products (live only; default preselected) ────────────────────────────
  const products = useDeviceProducts(deviceId || null);
  /** Edit mode: the booking's own product, while it is still live. Null once archived. */
  const bookedProduct = useMemo(
    () => (products.data ?? []).find((p) => p.id === bookedProductId) ?? null,
    [products.data, bookedProductId],
  );
  const product = useMemo(() => {
    const list = products.data ?? [];
    return (
      list.find((p) => p.id === productId) ?? bookedProduct ?? defaultDeviceProduct(list)
    );
  }, [products.data, productId, bookedProduct]);

  // ── Availability for the chosen day ──────────────────────────────────────
  const bounds = useMemo(() => dayBounds(dateYmd), [dateYmd]);
  const availability = useResourceAvailability(
    deviceId ? { deviceId, practiceId, from: bounds.from, to: bounds.to } : null,
  );
  const windows = useMemo(
    () => windowsForDay(parseAvailability(availability.data), bounds.day),
    [availability.data, bounds.day],
  );
  const notBookable = isNotBookable(availability.data);
  /**
   * The clock, read through state so the strip keeps telling the truth while
   * the rail is open (PROD-2444) — a window that goes at 11:00 stops being
   * offered at 11:00, not at the next unrelated re-render.
   */
  const now = useNow();

  /**
   * ── The bookable starts, READ from the backend ────────────────────────────
   * PROD-2455. Andrew, 2026-09-22: "can we do them exactly like the provider
   * booking appointment?"
   *
   * This rail used to ENUMERATE its own starts — `bookableStarts(windows, …)`
   * walked each `ResourceAvailability` window from the WINDOW START in
   * product-duration steps and then dropped the ones that had passed. The
   * backend anchors the same grid at NOW, rounded up, so the two disagreed on
   * every partly-spent window. Measured live on dev at 14:10, HBOT, window
   * 10:00–23:00: the backend offered 14:15, 15:15, 16:15 … and this rail
   * offered 15:00, 16:00, 17:00 …, silently losing 45 minutes of free machine
   * time that the rest of the product said was bookable, with no way to ask
   * for it. The practitioner rail never had the bug because it never derived a
   * list — it reads one from `Appointment/Slots`. This now does the same from
   * the device's own feed.
   *
   * `slotCount` is the whole remaining day, not the strip's three chips.
   *
   * `appointmentProfileId` is the CHOSEN product (PROD-2456), in create and in
   * edit mode alike — `product` already resolves to the pick, else the
   * booking's own product, else the default — so the grid steps by the
   * product being booked, exactly as the practitioner rail's does. The fetch
   * waits for the product list: firing before it would ask for the default
   * grid, paint it, and repaint a beat later once the product resolved.
   */
  const deviceSlots = useDeviceCalendarSlots(
    deviceId
      ? {
          organizationId,
          practiceId,
          startDate: dateYmd,
          endDate: dateYmd,
          devices: [deviceId],
          slotCount: WHOLE_DAY_SLOT_COUNT,
          appointmentProfileId: product?.id,
        }
      : null,
    !products.isPending,
  );
  const bookedStartIsOnShownDay = !!bookedStart && ymdLocal(bookedStart) === dateYmd;
  const slots = useMemo(() => {
    const free = toSlotItems(deviceRow(deviceSlots.data, deviceId)?.slots, now);
    // Edit mode: the booking's own slot, which the feed cannot offer (it is
    // taken — by this booking), stays on the list. See `withBookedStart`.
    if (!edit?.start || !edit.end || !bookedStartIsOnShownDay) return free;
    return withBookedStart(free, { start: edit.start, end: edit.end }, parseWallClock);
  }, [deviceSlots.data, deviceId, now, edit, bookedStartIsOnShownDay]);
  /**
   * "Nothing left today" and "nothing here at all" read the same to an
   * operator and mean different things — one says come back tomorrow, the
   * other says this device is booked solid. The slot feed answers only that
   * there is nothing; `ResourceAvailability` still knows whether every window
   * the device HAS on this day is already behind us, which is what separates
   * them. This is the job that keeps that query in the rail — it is not a
   * second source of bookable starts.
   */
  const allWindowsHavePassed =
    slots.length === 0 &&
    windows.length > 0 &&
    windows.every((w) => w.end.getTime() <= now.getTime());
  /**
   * Whether the picked start holds the chosen product inside a free window.
   * A chip always does — the feed is cut at that product's minutes — so this
   * matters for a Custom time and for the booked start in edit mode
   * (`durationGrew`). It validates a start the operator already chose; it
   * never produces one.
   */
  const startFits = !!start && !!product && fitsWindow(windows, start, product.minutesDuration);
  /**
   * Compare by INSTANT, not by string: a chip carries the feed's
   * `YYYY-MM-DDTHH:mm:ss` while a custom pick carries
   * `formatLocalDatetimeForWire`'s millisecond form, and the same minute must
   * highlight the chip either way.
   */
  const isSelectedSlot = (slot: SlotItem) => {
    if (!start) return false;
    const d = parseWallClock(slot.start);
    return !!d && d.getTime() === start.getTime();
  };
  /**
   * A picked start that has since passed. Kept apart from `startFits` on
   * purpose: "that window is taken" and "that window is over" are different
   * facts and the operator needs to be told which one they are looking at.
   */
  const startPassed = !!start && startHasPassed(start, now);
  /**
   * Edit mode: the booked start is usable as it stands even though the feed
   * and the windows call it busy — this booking is what is busy there — and
   * even when it has passed (a past session stays editable). Moving is judged
   * like booking: free, fitting and not over.
   */
  const isBookedStart = !!start && !!bookedStart && start.getTime() === bookedStart.getTime();
  const timeOk = !!start && (isBookedStart || (startFits && !startPassed));
  /**
   * A LONGER product at the booked start may overrun into the next booking —
   * only the server can know, so it is an advisory, not a block (carried over
   * from the retired edit rail).
   */
  const durationGrew =
    isBookedStart &&
    !startFits &&
    !!product &&
    product.minutesDuration > (edit?.minutesDuration ?? 0);
  const annotateSlot = edit
    ? (slot: SlotItem) =>
        bookedStart && parseWallClock(slot.start)?.getTime() === bookedStart.getTime()
          ? '· now'
          : null
    : undefined;

  // ── Patient search (same endpoint the create rail uses) ─────────────────
  const debouncedPatient = useDebounce(patientQuery);
  const patientResults = usePatientSearch({ organizationId, query: debouncedPatient });

  // ── Services — the full catalog; a device has no provider fees ──────────
  const debouncedService = useDebounce(serviceQuery);
  const patientId = edit ? patientIdOf(edit) : (patient?.id ?? null);
  const serviceFees = useServiceFees({
    providerId: null,
    locationId,
    patientId,
    type: 'all',
    query: debouncedService || undefined,
  });

  const book = useBookDevice();
  const update = useUpdateDeviceAppointment();
  const isSubmitting = edit ? update.isPending : book.isPending;

  // The client mirrors the server's rule exactly (PROD-2154, PROD-2165): the
  // product's own billing profile counts, and so does its own priced service,
  // so the client is never stricter than the server. Both are only READ here —
  // see the note in `buildDeviceBookingRequest` for why neither is sent back.
  const productBillingProfileId = product?.billingProfileId ?? null;
  const productServiceId = product?.serviceId ?? null;

  /**
   * The product's own priced service, resolved to a DISPLAYABLE row.
   *
   * Andrew, 2026-09-14: "when select device and appointment profile, please
   * select the service attached to it". The rail already SAID "Priced by
   * HBOT 60's service" and then left the operator staring at an unfiltered
   * catalogue (3510F, DNA-6, LW-046…) with no clue which row that was or what
   * it cost. This resolves the id to the real row so the price is on screen
   * before Book is pressed.
   *
   * Read from an UNFILTERED catalogue page rather than the section's own
   * search results: while the user types, that list narrows to the query and
   * the included row would flicker between its real label and a fallback.
   * With an empty search box the two hooks build the SAME query key
   * (`['service-fees', params]` with `query: undefined`), so React Query
   * dedupes them to one request; only an active search costs a second.
   *
   * The endpoint pages (50/page) and INNER JOINs `ServiceLocationFee`, so a
   * service that is off page 1 — or has no fee at this location — is simply
   * absent. That is a miss, not an error: the row then names the product it
   * comes from instead of inventing a price.
   */
  const productServiceCatalog = useServiceFees(
    // Edit mode also needs it to NAME the booking's existing services, which
    // arrive from the invoice as bare ids.
    productServiceId || edit
      ? {
          providerId: null,
          locationId,
          patientId,
          type: 'all',
        }
      : null,
  );
  const includedService = useMemo(() => {
    if (!productServiceId) return null;
    const hit = (productServiceCatalog.data?.results ?? []).find((s) => s.id === productServiceId);
    return { id: productServiceId, label: hit ? serviceLabel(hit) : null };
  }, [productServiceId, productServiceCatalog.data]);
  const labelFor = useMemo(() => {
    const byId = new Map<string, string>();
    for (const svc of [...(productServiceCatalog.data?.results ?? []), ...(serviceFees.data?.results ?? [])]) {
      if (svc.id && !byId.has(svc.id)) byId.set(svc.id, serviceLabel(svc));
    }
    return byId;
  }, [productServiceCatalog.data, serviceFees.data]);
  /**
   * Edit mode: the booking's services as it bills them, minus its product's
   * own (`seedDeviceEditServices`). An ARCHIVED product is not found, so its
   * service stays as an ordinary removable chip rather than vanishing.
   */
  const seedChips = useMemo<SelectedService[]>(
    () =>
      edit
        ? seedDeviceEditServices(edit, bookedProduct).map((id) => ({ id, label: labelFor.get(id) ?? id }))
        : [],
    [edit, bookedProduct, labelFor],
  );
  const chosenServices = services ?? seedChips;
  const updateServices = (fn: (cur: SelectedService[]) => SelectedService[]) =>
    setServices((cur) => fn(cur ?? seedChips));

  /**
   * What actually ships as `addedServices` — the hand-picked services MINUS
   * the product's own one. The product's service is SHOWN (the included row
   * below) and never SENT.
   *
   * On the double-billing question, checked rather than assumed: it would NOT
   * double-bill. `CreateAppointmentCommand.ResolveDeviceBookingAsync`
   * (Scheduling `origin/master`) folds the product's service into the same
   * list the caller's services travel in, and says so in as many words —
   * "UNION, not overwrite and not append … a union cannot double-count the
   * product's own service if the caller happened to send it as well" — with
   * `AddedServices.Distinct()` in Billing's `GetIndividualServices` behind it
   * and a pinned regression test named
   * `A_Caller_Resending_The_Products_Own_Service_Is_Not_Double_Counted`. The
   * two-line dev invoice 30617b48 (LW-017 750 + LW-018 950) is that union
   * working, not a duplicate: one line for the product, one for the extra.
   *
   * It is still not sent, for the reason already written on
   * `DeviceAppointmentProfile.serviceId`: the server re-reads the product for
   * itself, so echoing a snapshot from a cached product list would pin a price
   * the practice may have changed since — and the PROD-2175 tests pin that the
   * id never appears on the wire. Filtering here also keeps the UI honest (the
   * service is shown once, as included, not twice) and keeps the rail correct
   * against any Scheduling build that predates that union.
   *
   * Derived, not stored, which settles what happens when the product changes:
   * switch HBOT 30 → HBOT 90 and the included row swaps with it, nothing of
   * the old product is left behind, and a service the user added by hand is
   * untouched — if the new product happens to include it, it moves into the
   * included row and back out again if they switch away.
   */
  const addedServices = useMemo(
    () => chosenServices.filter((s) => s.id !== productServiceId),
    [chosenServices, productServiceId],
  );

  const invoiceable = deviceBookingIsInvoiceable({
    serviceIds: addedServices.map((s) => s.id),
    billingProfileId: productBillingProfileId,
    productServiceId,
  });
  const missing: string[] = [];
  if (!device) missing.push('device');
  if (!product) missing.push('product');
  if (edit ? !timeOk : !start || !startFits || startPassed) missing.push('time');
  if (!patientId) missing.push('patient');
  // An existing booking already has its invoice; the edit rail never gated on
  // this, and the edit keeps that (the server carries the product's line).
  if (!edit && !invoiceable) missing.push('a service to invoice');
  // True once a product is chosen and it prices itself, by EITHER pointer —
  // drives the copy below.
  const pricedByProduct = !!product && (!!productServiceId || !!productBillingProfileId);
  if (!edit && !location) missing.push('location');
  const canBook = missing.length === 0 && !isSubmitting && !locked;
  const productChanged = !!edit && !!product && product.id !== bookedProductId;
  const timeChanged = !!edit && !!start && !!bookedStart && start.getTime() !== bookedStart.getTime();
  /** A session of a recurring course — a time change asks which sessions it reaches. */
  const course = edit?.recurring?.id ? edit.recurring : null;
  // The user cannot act on a missing location any more — there is no control
  // to fix it with — so it gates Book but never appears in the "Still needed"
  // copy. A practice with no locations at all gets the explicit line below
  // instead, which names the thing an admin has to go and create.
  const visibleMissing = missing.filter((m) => m !== 'location');

  /**
   * A recurrence was checked for ONE device, product and first session. Change
   * any of them and its sessions no longer describe what would be booked, so it
   * is dropped rather than silently booked against the old answer. Done in the
   * handlers that change them, not in an effect.
   */
  const clearRecurrence = () => {
    if (getValues('recurring')) setValue('recurring', null, { shouldValidate: true });
  };

  const resetForDevice = (id: string) => {
    setDeviceId(id);
    setProductId(null);
    setPickedStart(null);
    setRefusal(null);
    clearRecurrence();
  };

  const pickStart = (picked: Date) => {
    setPickedStart(picked);
    setRefusal(null);
    clearRecurrence();
  };

  /**
   * Recurrence is offered on the same terms as the practitioner rail's
   * (provider + profile + billing + slot + patient): here the device, its
   * product, something to invoice, a bookable first session and the patient.
   */
  const recurrenceEnabled =
    !!device && !!product && !!start && startFits && !startPassed && !!patient && invoiceable;

  const openRecurrence = () => {
    if (!device || !product || !start) return;
    // The configurator reads the first session off the form it shares with the
    // practitioner rail; this rail keeps its own state, so it hands it over.
    setValue('slotStart', localNoZ(start));
    setValue('slotEnd', localNoZ(new Date(start.getTime() + product.minutesDuration * 60_000)));
    setValue('minutesDuration', product.minutesDuration);
    setView('recurrence');
  };

  const close = () => onOpenChange(false);

  const submit = () => {
    if (edit) {
      submitEdit();
      return;
    }
    if (!device || !product || !start || !patient || !location) return;
    // The write path refuses a past start on its own rather than trusting the
    // button to have stayed disabled: the tick that retires a window and a
    // click can land in either order.
    if (startHasPassed(start, new Date())) return;
    setRefusal(null);
    const course = toDeviceRecurring(recurring);
    const request = buildDeviceBookingRequest(
      {
        device,
        product,
        patientId: patient.id,
        start,
        location,
        // NOT `services` — see `addedServices` above: the product's own
        // service is billed by the server and must not ride along here.
        serviceIds: addedServices.map((s) => s.id),
        // The shared NoteSection owns the note; the wire field is unchanged.
        comment: getValues('comment'),
      },
      { organizationId, practiceId, createdBy: userId },
    );
    if (course) request.recurring = course;
    book.mutate(request, {
      onSuccess: (res) => {
        toast.success(
          course
            ? `${course.slots.length} × ${product.profileName} booked on ${device.name}`
            : `${product.profileName} booked on ${device.name}`,
        );
        onBooked?.(res.id);
        onOpenChange(false);
      },
      onError: (err) => handleRefusal(err),
    });
  };

  /** One refusal path for booking and editing — the server speaks the same codes on both routes. */
  const handleRefusal = async (err: unknown) => {
    const classified = classifyDeviceBookingFailure(err);
    // The catalogue's fallback copy is written for BOOKING ("The device
    // couldn't be booked"), which would be a lie on an edit.
    const f =
      edit && classified.code === 'Unknown' && classified.status !== 400
        ? { ...classified, message: "This booking couldn't be saved. Please try again." }
        : classified;
    if (!start) return;
    if (f.code === 'RecurringPartiallyBooked') {
      // Part of the course IS on the calendar (the hook refreshed it). The
      // recurrence that produced it is spent — booking it again would
      // collide with its own sessions — so it goes, and the alert says
      // exactly how many sessions were kept.
      clearRecurrence();
      setRefusal({ ymd: dateYmd, failure: f, nextFree: null });
      return;
    }
    if (f.code !== 'ResourceBooked') {
      setRefusal({ ymd: dateYmd, failure: f, nextFree: null });
      return;
    }
    // The slot went meanwhile — re-read the SLOT FEED (not the availability
    // windows: since PROD-2455 the feed, not this client, is what decides
    // what is free) and offer the next free start after the refused one.
    const fresh = await deviceSlots.refetch();
    const freshStarts = toSlotItems(deviceRow(fresh.data, deviceId)?.slots, new Date())
      .map((s) => parseWallClock(s.start))
      .filter((d): d is Date => !!d);
    setRefusal({
      ymd: dateYmd,
      failure: f,
      nextFree: freshStarts.find((d) => d.getTime() > start.getTime()) ?? null,
    });
    setPickedStart(null);
  };

  /**
   * PROD-2526 — save an edit through the DEVICE route. `scope` is the answer
   * to the course prompt; `this-only` (and every non-course booking) edits this
   * session alone, exactly as `editRecurring: null` does on the provider route.
   */
  const saveEdit = (scope: RecurringScope) => {
    if (!edit || !product || !start) return;
    setRefusal(null);
    const series =
      scope !== 'this-only' && course
        ? { editRecurring: buildEditRecurringPayload(scope, edit.start, ['start', 'end']) }
        : null;
    const body = buildDeviceUpdateRequest(
      edit,
      {
        product,
        start,
        extraServiceIds: addedServices.map((s) => s.id),
        comment: getValues('comment'),
      },
      userId,
      series,
    );
    update.mutate(
      { appointmentId: edit.id, body },
      {
        onSuccess: () => {
          toast.success(
            series ? 'Series rescheduled' : `${product.profileName} updated on ${device?.name ?? 'the device'}`,
          );
          setScopeOpen(false);
          onSaved?.(body.start);
          if (closeOnSaveSuccess) onOpenChange(false);
        },
        onError: (err) => {
          setScopeOpen(false);
          void handleRefusal(err);
        },
      },
    );
  };

  const submitEdit = () => {
    if (!edit || !product || !start || locked) return;
    // A course session asks which sessions the TIME change reaches — the
    // practitioner series menu's three choices. A change that keeps the time
    // (note, services, product) is this session's alone and saves directly.
    if (course && timeChanged) {
      setScopeOpen(true);
      return;
    }
    saveEdit('this-only');
  };

  // PROD-2383 — the recurrence configurator REPLACES the rail body, as it does
  // on the practitioner rail. Placed after every hook, so the switch cannot
  // change the hook order. The device and product are required to open it.
  if (view === 'recurrence' && device && product) {
    return (
      <RecurrencePickerView
        actor={{ kind: 'device', deviceId: device.id, deviceProfileId: product.id, practiceId }}
        onClose={() => setView('book')}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header — the SHARED rail header: Cancel · title · primary action. */}
      <PanelHeader
        mode={edit ? 'edit' : 'create'}
        title={
          edit
            ? editIntent === 'reschedule'
              ? 'Reschedule Device Booking'
              : 'Edit Device Booking'
            : 'Book a Device'
        }
        saveLabel={edit ? 'Save' : 'Book'}
        savingLabel={edit ? 'Saving…' : 'Booking…'}
        isValid={canBook}
        isSubmitting={isSubmitting}
        onClose={close}
        onSave={submit}
      />

      {/* Scrollable body — the ONLY thing that scrolls. `flex-1 min-h-0`
          (not a viewport fraction) is what keeps the footer on screen at
          every window height. */}
      <div
        data-testid="book-device-body"
        className="flex-1 min-h-0 overflow-y-auto scrollbar-hide"
      >
        <div className="pb-2 flex flex-col text-sm">
          <p className="px-6 pt-4 text-xs text-text-muted">
            {edit
              ? 'Change the product, the time, the services or the note. The device and the patient stay as booked, and no practitioner is involved.'
              : 'Choose the device and its product, pick a free window, then the patient. No practitioner is involved.'}
          </p>

          {locked && edit ? (
            <p data-testid="book-device-locked" className="px-6 pt-3 text-xs text-[var(--status-bad)]">
              This booking is {edit.status?.display ?? edit.status?.code} — it can no longer be edited.
            </p>
          ) : null}

          {/* The location is no longer asked for (PROD-2193) — but it is
              still REQUIRED on the wire, so a practice with none at all has
              to be told, not silently left with a dead Book button. */}
          {!edit && !location ? (
            <p className="px-6 pt-3 text-xs text-[var(--status-bad)]">
              This practice has no location, so a device booking can't be priced.
              Add one under Settings › Practice › Locations.
            </p>
          ) : null}

          {/* 1 — Date — the SHARED date strip, the same control the create
              rail shows in its no-provider state. */}
          <DatePickerSection />

          {/* 2 — Device — stands exactly where ProviderSection stands, and
              since PROD-2193 wears the same control: search over cards, one
              card per device carrying the name and technician. 57 devices do
              not fit in a native select. */}
          <div className="px-4 py-3 border-t border-border-subtle">
            <p
              id="book-device-device-label"
              className="mb-2 block text-[length:var(--type-meta-size)] font-medium text-text-muted"
            >
              Device
            </p>
            <div role="group" aria-labelledby="book-device-device-label">
              {/* Edit mode: the device is FIXED — moving a booking to another
                  machine is a cancel plus a booking (DeviceImmutable). */}
              <DevicePickerGrid
                devices={devices}
                selected={device}
                readOnly={!!edit}
                onSelect={(d) => resetForDevice(d.id)}
                onClear={() => resetForDevice('')}
              />
            </div>
          </div>

          {/* 3 — Product (the device's appointment profiles) */}
          {device ? (
            <div className="px-4 py-3 border-t border-border-subtle">
              <Field label="Product">
                {products.isLoading ? (
                  <p className="text-text-muted">Loading products…</p>
                ) : products.isError ? (
                  <p className="text-[var(--status-bad)]">Couldn't load this device's products.</p>
                ) : (products.data ?? []).length === 0 ? (
                  <p className="text-text-muted">
                    This device has no live products — add one under Settings › Devices › Schedule.
                  </p>
                ) : (
                  <fieldset className="flex flex-wrap gap-2 border-0 p-0 m-0" disabled={locked}>
                    <legend className="sr-only">Product</legend>
                    {(products.data ?? []).map((p) => {
                      const selected = product?.id === p.id;
                      return (
                        <label key={p.id} className={cn(CHIP_BASE, selected ? CHIP_ON : CHIP_OFF)}>
                          <input
                            type="radio"
                            name="book-device-product"
                            value={p.id}
                            checked={selected}
                            onChange={() => {
                              setProductId(p.id);
                              // A course was checked for the old product's duration.
                              clearRecurrence();
                              // The picked start is deliberately NOT thrown
                              // away here. Andrew, 2026-09-14: "when I select
                              // the appointment profile of duration 90 mins it
                              // appears in the booking panel not selected" —
                              // the chip was right, the BOOKING was not. This
                              // used to `setPickedStart(null)`, so choosing the
                              // 90 wiped a 10:00 that still had 09:00–12:00
                              // around it (10:00 + 90 = 11:30, well inside it)
                              // and sent the rail back to "Still needed: time"
                              // the instant the product was picked, which reads
                              // as the pick not taking. It also fought the
                              // `starts` memo above, whose whole job is to keep
                              // offering an off-step start that still fits.
                              // `start` is already fully derived-guarded:
                              // `startFits` gates Book, and a start the new
                              // duration genuinely cannot fit renders the
                              // explicit "isn't a free N-minute window" line
                              // rather than blanking in silence. Only the
                              // refusal goes — it was about the old duration.
                              setRefusal(null);
                            }}
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: p.color ?? 'var(--color-brand-primary)' }}
                          />
                          {p.profileName}
                          <span className="text-xs text-text-muted">{p.minutesDuration} min</span>
                          {edit ? (
                            p.id === bookedProductId ? <span className="text-xs text-text-hint">booked</span> : null
                          ) : p.isDefault ? (
                            <span className="text-xs text-text-hint">default</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </fieldset>
                )}
                {/* A booking whose product has since been ARCHIVED: its chip is
                    gone from the live list, so say so rather than silently
                    preselecting a different product's price. */}
                {edit && bookedProductId && !bookedProduct && !products.isLoading ? (
                  <p data-testid="book-device-archived-product" className="mt-2 text-xs text-[var(--status-bad)]">
                    The product this booking was made on is no longer live. Saving will move it onto the
                    product selected above.
                  </p>
                ) : null}
              </Field>
            </div>
          ) : null}

          {/* 4 — Time (stands where TimeSlotsSection stands).
              Since PROD-2455 this section is the practitioner rail's: a
              SERVER-derived list, a SELECTED SLOT card with a Custom entry,
              and Morning / Afternoon / Evening grouping — all through the
              shared components rather than a second implementation. */}
          {device && product ? (
            <div className="px-4 py-3 border-t border-border-subtle">
              <Field label="Time">
                {/* SELECTED SLOT — the practitioner rail's card, and the home
                    of the Custom link: a start off the server's grid, which is
                    cut at the chosen product's minutes (PROD-2456). Safe by
                    contract — the booking sends
                    `start` only and the server derives `end` from the product
                    that was picked. */}
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-medium text-text-muted">
                    Selected slot
                  </span>
                  {locked ? null : <CustomTimePopover
                    pickerDate={bounds.day}
                    durationMinutes={product.minutesDuration}
                    initial={start ? localNoZ(start) : null}
                    onConfirm={(slot) => {
                      const picked = parseWallClock(slot.start);
                      if (!picked) return;
                      pickStart(picked);
                    }}
                  />}
                </div>
                <p
                  data-testid="book-device-selected-slot"
                  className="mb-3 rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] px-3 py-2 [font:var(--type-body)] text-[var(--ink-body)]"
                >
                  {start ? formatClock(start) : '— : —'}
                  <span className="ml-2 text-xs text-text-muted">
                    {product.minutesDuration} min
                  </span>
                </p>

                {availability.isLoading || deviceSlots.isLoading ? (
                  <p className="text-text-muted">Checking availability…</p>
                ) : availability.isError || deviceSlots.isError ? (
                  <p className="text-[var(--status-bad)]">Couldn't read this device's availability.</p>
                ) : notBookable && !(edit && bookedStartIsOnShownDay) ? (
                  <p className="text-text-muted">
                    Not bookable — this device has no working hours. Set them under Settings › Devices ›
                    Schedule.
                  </p>
                ) : slots.length === 0 ? (
                  <p className="text-text-muted">
                    {allWindowsHavePassed
                      ? 'Every bookable time on this day has passed.'
                      : 'No bookable time left on this day.'}
                  </p>
                ) : (
                  <fieldset className="border-0 p-0 m-0" disabled={locked}>
                    <SlotGroupList
                      slots={slots}
                      name="book-device-window"
                      legend="Free windows"
                      isSelected={isSelectedSlot}
                      annotate={annotateSlot}
                      onPick={(slot) => {
                        const picked = parseWallClock(slot.start);
                        if (!picked) return;
                        pickStart(picked);
                      }}
                    />
                  </fieldset>
                )}
                {durationGrew && start ? (
                  <p data-testid="book-device-duration-warning" className="mt-2 text-xs text-text-muted">
                    {product.profileName} is longer than the current booking. The device has to be free for the
                    whole {product.minutesDuration} minutes from {formatClock(start)} or the save is refused.
                  </p>
                ) : null}
                {edit && !start && !availability.isLoading ? (
                  <p className="mt-2 text-xs text-text-muted">
                    This booking is on another day. Pick a window to move it here, or go back to its own day.
                  </p>
                ) : null}
                {isBookedStart ? null : start && startPassed && !availability.isLoading ? (
                  <p className="mt-2 text-xs text-[var(--status-bad)]">
                    {formatClock(start)} has already passed — pick a later window.
                  </p>
                ) : start && !startFits && !availability.isLoading ? (
                  <p className="mt-2 text-xs text-[var(--status-bad)]">
                    {formatClock(start)} isn't a free {product.minutesDuration}-minute window — pick one above.
                  </p>
                ) : null}
              </Field>
            </div>
          ) : null}

          {/* 5 — Patient */}
          <div className="px-4 py-3 border-t border-border-subtle">
            <Field label="Patient" htmlFor={edit ? undefined : 'book-device-patient'}>
              {edit ? (
                // FIXED on an existing booking, as the practitioner rail fixes it (PatientImmutable).
                <p data-testid="book-device-fixed-patient" className="text-text-primary">
                  {patientLabelOf(edit)}
                </p>
              ) : patient ? (
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-[var(--accent-active-bg)] px-2 py-1 [font:var(--type-body)] text-[var(--ink-body)]">
                    {patientLabel(patient)}
                    {patient.mrn ? <span className="ml-1 text-xs text-text-muted">{patient.mrn}</span> : null}
                  </span>
                  <button
                    type="button"
                    aria-label="Change patient"
                    onClick={() => setPatient(null)}
                    className="rounded p-1 text-text-muted hover:text-text-primary hover:bg-grey-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
                    <input
                      id="book-device-patient"
                      placeholder="Search patients (name, MRN, phone)"
                      value={patientQuery}
                      onChange={(e) => setPatientQuery(e.target.value)}
                      className="h-10 w-full rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] pl-8 pr-3 [font:var(--type-body)] text-[var(--ink-body)] focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    />
                  </div>
                  {debouncedPatient.trim().length >= 2 ? (
                    <ul className="mt-1 max-h-40 overflow-y-auto scrollbar-hide rounded-card border border-[var(--surface-line)] divide-y divide-border-subtle">
                      {(patientResults.data ?? []).length === 0 && !patientResults.isFetching ? (
                        <li className="px-3 py-2 text-text-muted">No patients match.</li>
                      ) : null}
                      {(patientResults.data ?? []).map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setPatient(p);
                              setPatientQuery('');
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-grey-100 text-text-primary"
                          >
                            {patientLabel(p)}
                            {p.mrn ? <span className="ml-2 text-xs text-text-muted">{p.mrn}</span> : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </>
              )}
            </Field>
          </div>

          {/* 6 — Services (make the booking invoiceable) */}
          <div className="px-4 py-3 border-t border-border-subtle">
            <Field label="Services" htmlFor="book-device-service">
              <p className="mb-2 text-xs text-text-muted">
                {pricedByProduct
                  ? `Priced by ${product.profileName}'s ${productServiceId ? 'service' : 'billing profile'}. Add services only if this session needs extras.`
                  : 'A device booking is invoiced when it is made — add at least one service.'}
              </p>
              {/* The product's own service, shown as a SELECTED row — the
                  thing Andrew asked for ("please select the service attached
                  to it"). It wears the selected chip's treatment and a tick so
                  it reads as chosen, and says which product it came from so it
                  is obvious why there is no × on it. There is no × because
                  removing it would change nothing: it is not in the payload,
                  the server prices it from the product either way, and a
                  control that cannot affect the invoice is worse than no
                  control. The way to change it IS to change the product. */}
              {includedService ? (
                <ul className="mb-2 flex flex-wrap gap-2">
                  <li
                    data-testid="book-device-included-service"
                    className="inline-flex items-center gap-1 rounded-md border border-brand-primary bg-[var(--accent-active-bg)] px-2 py-1 text-xs text-text-primary"
                  >
                    <Check className="h-3 w-3 flex-shrink-0 text-brand-primary" aria-hidden="true" />
                    {includedService.label ?? 'Service on this product'}
                    <span className="text-text-muted">
                      · included in {product?.profileName}
                    </span>
                  </li>
                </ul>
              ) : null}
              {addedServices.length > 0 ? (
                <ul className="mb-2 flex flex-wrap gap-2">
                  {addedServices.map((s) => (
                    <li key={s.id} className="inline-flex items-center gap-1 rounded-md bg-[var(--accent-active-bg)] px-2 py-1 text-xs text-text-primary">
                      {labelFor.get(s.id) ?? s.label}
                      <button
                        type="button"
                        aria-label={`Remove ${s.label}`}
                        disabled={locked}
                        onClick={() => updateServices((cur) => cur.filter((x) => x.id !== s.id))}
                        className="rounded p-1 text-text-muted hover:text-text-primary disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
                <input
                  id="book-device-service"
                  placeholder="Search the service catalog"
                  value={serviceQuery}
                  disabled={locked}
                  onChange={(e) => setServiceQuery(e.target.value)}
                  className="h-10 w-full rounded-card border border-[var(--surface-line)] bg-[var(--surface-read)] pl-8 pr-3 [font:var(--type-body)] text-[var(--ink-body)] focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
                />
              </div>
              <ul className="mt-1 max-h-40 overflow-y-auto scrollbar-hide rounded-card border border-[var(--surface-line)] divide-y divide-border-subtle">
                {serviceFees.isLoading ? (
                  <li className="px-3 py-2 text-text-muted">Loading services…</li>
                ) : (serviceFees.data?.results ?? []).length === 0 ? (
                  <li className="px-3 py-2 text-text-muted">No services match.</li>
                ) : (
                  (serviceFees.data?.results ?? []).map((svc, i) => {
                    const id = svc.id ?? '';
                    // The product's own service is already on the booking, so
                    // it is READ-ONLY here rather than addable: a second tick
                    // on it could not change the invoice (the server unions it
                    // in either way), and offering an action with no effect is
                    // the confusing part. Hiding the row instead would leave
                    // the operator hunting for a service that is quietly gone.
                    const included = !!id && id === productServiceId;
                    const added = addedServices.some((s) => s.id === id);
                    const label = serviceLabel(svc);
                    return (
                      <li key={id || `svc-${i}`}>
                        <button
                          type="button"
                          disabled={!id || included || locked}
                          aria-pressed={added || included}
                          onClick={() =>
                            updateServices((cur) =>
                              added ? cur.filter((s) => s.id !== id) : [...cur, { id, label }],
                            )
                          }
                          className={cn(
                            'w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-grey-100 text-text-primary disabled:opacity-50',
                            // Included reads as CHOSEN, not as broken: it takes
                            // the selected tint and keeps full opacity, so the
                            // only faded row left is the genuinely unusable one
                            // (a catalog entry that arrived with no id).
                            included && 'bg-[var(--accent-active-bg)] hover:bg-[var(--accent-active-bg)] disabled:opacity-100',
                          )}
                        >
                          <span className="truncate">{label}</span>
                          {included ? (
                            <span className="flex-shrink-0 text-size-xs text-text-muted">
                              Included
                            </span>
                          ) : added ? (
                            <Check className="h-4 w-4 text-brand-primary flex-shrink-0" />
                          ) : (
                            <Plus className="h-4 w-4 text-text-muted flex-shrink-0" />
                          )}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </Field>
          </div>

          {/* 7 — Note — the SHARED internal-note card. */}
          <NoteSection />

          {/* 8 — Recurrence (PROD-2383). The practitioner rail's summary card
              once a course is set (edit / clear), and its Recurrence
              action otherwise, gated the same way. */}
          {edit ? null : recurring ? (
            <RecurrenceSection onEdit={openRecurrence} />
          ) : (
            <div className="px-4 py-3 border-t border-border-subtle">
              <button
                type="button"
                onClick={recurrenceEnabled ? openRecurrence : undefined}
                disabled={!recurrenceEnabled}
                className={cn(
                  'flex w-full flex-col items-center gap-1 py-2 rounded-card border transition-colors',
                  recurrenceEnabled
                    ? 'border-[var(--surface-line)] text-text-muted hover:border-brand-primary hover:text-brand-primary'
                    : 'border-border-subtle text-text-hint cursor-not-allowed opacity-60',
                )}
                title={recurrenceEnabled ? 'Recurrence' : 'Recurrence (pick the device, product, time and patient first)'}
              >
                <Repeat className="h-4 w-4" />
                <span className="text-xs font-medium">Recurrence</span>
              </button>
            </div>
          )}

          {failure ? (
            <div className="px-4 py-3">
              <div role="alert" className="rounded-card border border-red-500 bg-red-100 px-3 py-2 text-sm text-[var(--status-bad)]">
                <p>{failure.message}</p>
                {failure.code === 'ResourceBooked' && product ? (
                  nextFree ? (
                    <button
                      type="button"
                      onClick={() => pickStart(nextFree)}
                      className="mt-1 font-semibold underline"
                    >
                      Next free window: {formatClock(nextFree)} — use it
                    </button>
                  ) : (
                    <p className="mt-1 text-xs">No later {product.minutesDuration}-minute window on this day.</p>
                  )
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* What's still missing wins over the summary: with only a service
          outstanding the old order showed the happy-path summary and left the
          reason for the disabled Book in a hover title. Kept as a VISIBLE
          strip above the footer rather than folded into a tooltip. */}
      <p className="flex-shrink-0 px-4 py-2 border-t border-[var(--surface-line)] text-xs text-text-muted">
        {visibleMissing.length > 0
          ? `Still needed: ${visibleMissing.join(', ')}`
          : device && product && start
            ? `${product.profileName} · ${device.name} · ${formatClock(start)}` +
              (recurring?.slots?.length ? ` · ${recurring.slots.length} sessions` : '') +
              (productChanged || timeChanged ? ' · changed' : '')
            : ''}
      </p>

      {/* Footer — the SHARED rail footer. A flex-shrink-0 sibling of the
          scroller in an h-full column: it cannot be pushed off screen. */}
      <PanelFooter
        mode={edit ? 'edit' : 'create'}
        labels={edit ? { save: 'Save Changes', saving: 'Saving…' } : { save: 'Book Device', saving: 'Booking…' }}
        isSubmitting={isSubmitting}
        isValid={canBook}
        missingFields={visibleMissing}
        onSave={submit}
        onCancel={close}
      />

      {edit ? (
        <DeviceSeriesScopeDialog
          open={scopeOpen}
          onOpenChange={setScopeOpen}
          productChanged={productChanged}
          isSubmitting={update.isPending}
          onConfirm={saveEdit}
        />
      ) : null}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-2 block text-[length:var(--type-meta-size)] font-medium text-text-muted"
      >
        {label}
      </label>
      {children}
    </div>
  );
}
